import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";
import { useState } from "react";

// Official Google "G" mark — brand guidelines require the multi-color glyph
// on sign-in buttons.
function GoogleG() {
  return (
    <svg viewBox="0 0 48 48" className="size-4 shrink-0" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

// Microsoft's four-square mark — brand guidelines require the multi-color
// glyph on sign-in buttons.
function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 23 23" className="size-4 shrink-0" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

type SocialButtonProps = {
  next?: string;
  // Preselects this account in the provider's chooser (and skips it when the
  // user is only signed into that one). Set when the entry link is bound to
  // an identity — an invitation or a waitlist approval — so a social sign-in
  // doesn't quietly land them on a different account.
  loginHint?: string;
  onError: (message: string) => void;
};

/**
 * Shared "Continue with <provider>" core — kicks off the better-auth social
 * flow. The browser leaves for the provider and returns via the backend
 * callback with a session cookie; the route guards then send onboarding
 * users to /onboarding exactly like the email-OTP path.
 */
function SocialSignInButton({
  provider,
  label,
  logo,
  next,
  loginHint,
  onError,
}: SocialButtonProps & {
  provider: "google" | "microsoft";
  label: string;
  logo: React.ReactNode;
}) {
  const [redirecting, setRedirecting] = useState(false);

  async function handleClick() {
    setRedirecting(true);
    const { error } = await authClient.signIn.social({
      provider,
      callbackURL: `${window.location.origin}${next || "/"}`,
      errorCallbackURL: `${window.location.origin}/login`,
      ...(loginHint ? { loginHint } : {}),
    });
    if (error) {
      onError(error.message ?? `${label} failed`);
      setRedirecting(false);
    }
    // On success the browser navigates away — no state to reset.
  }

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      onClick={handleClick}
      disabled={redirecting}
    >
      {logo}
      {redirecting ? "Redirecting…" : label}
    </Button>
  );
}

export function GoogleSignInButton(props: SocialButtonProps) {
  return (
    <SocialSignInButton
      provider="google"
      label="Continue with Google"
      logo={<GoogleG />}
      {...props}
    />
  );
}

export function MicrosoftSignInButton(props: SocialButtonProps) {
  return (
    <SocialSignInButton
      provider="microsoft"
      label="Continue with Microsoft"
      logo={<MicrosoftLogo />}
      {...props}
    />
  );
}

export function AuthMethodDivider() {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-border" />
      <span className="text-xs text-muted-foreground">OR</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}
